import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'form', 'newBuildHouse', 'newBuildFlat',
    'propertyRefurbishment', 'propertyRefurbishmentDiv', 'propertyOutbuildings', 'outbuildingsDiv',
    'propertyLandsize', 'landsizeDiv',
    'propertyTenure', 'propertyTenureDiv', 'propertyTimeleft', 'propertyTimeleftDiv',
    'sameReport', 'reportFormatDiv', 'reInspection', 'reInspectionDiv',
    'inspectionCount', 'inspectionCountDiv',
    'fixedFee', 'fixedFeeContainer',
    'lenderAddressee', 'lenderAddresseeDiv', 'lenderEntity',
    'ledByApi', 'apiAccessDiv',
    'quoteRequestor', 'lender', 'lenderContactsDiv', 'brokerContactsDiv',
    'lenderEntitiesFrame', 'lenderContacts',
    'broker', 'brokerContacts', 'conflictStatus', 'conflictStatusNotes', 'displayConflictStatusNotes',
    'applicantName', 'applicantCompany'
  ];
  static values = {
    valuationId: Number,
    ledByApiMessage: String,
    ledByApiOriginal: String
  }

  connect() {
    this.refreshBoundActions = this.refreshBoundActions.bind(this)
    document.addEventListener("turbo:frame-load", this.refreshBoundActions)

    this.fixedFeeTarget.addEventListener('change', () => this.toggleFixedFee());
    this.ledByApiTarget.addEventListener('change', () => this.toggleApiAccess());

    this.toggleCondition()
    this.toggleOutbuildings()
    this.toggleLandsize()
    this.toggleTenure()
    this.toggleReportFormatDetails()
    this.toggleReInspectionDetails()
    this.toggleInspectionCount()
    this.toggleFixedFee()
    this.toggleApiAccess()

    // Initialize contact fields options on edit mode
    this.initializeContactOptions()

    this.handleFormValidationErrors()
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.refreshBoundActions)
  }

  refreshBoundActions(event) {
    const frame = event.target
    if (this.element.contains(frame)) {
      try {
        this.application.load()
      } catch (e) {
        console.error('Error in refreshBoundActions:', e)
      }
    }
  }

  toggleNewBuild(event) {
    const checkbox = event.target
    const isHouse = checkbox === this.newBuildHouseTarget

    if (checkbox.checked) {
      if (isHouse) {
        this.newBuildFlatTarget.checked = false
      } else {
        this.newBuildHouseTarget.checked = false
      }
    }
  }

  toggleCondition() {
    if (!this.hasPropertyRefurbishmentTarget) return

    const refurbishment_value = this.propertyRefurbishmentTarget.value
    if (['light', 'heavy'].includes(refurbishment_value)) {
      this.propertyRefurbishmentDivTarget.classList.remove('hidden')
    } else {
      this.propertyRefurbishmentDivTarget.classList.add('hidden')
    }
  }

  toggleOutbuildings() {
    if (!this.hasPropertyOutbuildingsTarget) return

    const value = this.propertyOutbuildingsTarget.value
    if (value === 'true') {
      this.outbuildingsDivTarget.classList.remove('hidden')
    } else {
      this.outbuildingsDivTarget.classList.add('hidden')
    }
  }

  toggleLandsize() {
    if (!this.hasPropertyLandsizeTarget) return

    const value = this.propertyLandsizeTarget.value
    if (value === 'over_two_acres') {
      this.landsizeDivTarget.classList.remove('hidden')
    } else {
      this.landsizeDivTarget.classList.add('hidden')
    }
  }

  toggleTenure() {
    if (!this.hasPropertyTenureTarget) return

    const tenure_value = this.propertyTenureTarget.value
    const timeleft_value = this.propertyTimeleftTarget.value
    const tenure = this.propertyTenureDivTarget
    const timeleft = this.propertyTimeleftDivTarget
    if (tenure_value === '2' && timeleft_value === 'true') {
      tenure.classList.remove('hidden')
      timeleft.classList.remove('hidden')
    } else if (tenure_value === '2' && timeleft_value !== 'true') {
      tenure.classList.remove('hidden')
      timeleft.classList.add('hidden')
    } else {
      tenure.classList.add('hidden')
      timeleft.classList.add('hidden')
    }
  }

  toggleReportFormatDetails() {
    const type = this.sameReportTarget.value
    if (type === 'false') {
      this.reportFormatDivTarget.classList.remove('hidden')
    } else {
      this.reportFormatDivTarget.classList.add('hidden')
    }
  }

  toggleReInspectionDetails() {
    const type = this.reInspectionTarget.value
    if (type === 'true') {
      this.reInspectionDivTarget.classList.remove('hidden')
    } else {
      this.reInspectionDivTarget.classList.add('hidden')
    }
  }

  toggleInspectionCount() {
    const type = this.inspectionCountTarget.value
    if (type === 'initial_and_interim_reports') {
      this.inspectionCountDivTarget.classList.remove('hidden')
    } else {
      this.inspectionCountDivTarget.classList.add('hidden')
    }
  }

  toggleFixedFee() {
    if (this.fixedFeeTarget.checked) {
      this.fixedFeeContainerTarget.classList.remove('hidden')
    } else {
      this.fixedFeeContainerTarget.classList.add('hidden')
    }
  }

  toggleApiAccess() {
    try {
      if (this.ledByApiTarget.checked) {
        this.apiAccessDivTarget.classList.remove('hidden')
      } else {
        this.apiAccessDivTarget.classList.add('hidden')
      }
    } catch (e) {
      console.log(e)
    }
  }

  handleFormValidationErrors() {
    requestAnimationFrame(() => {
      if (!this.hasFormTarget) return

      const errorFields = this.formTarget.querySelectorAll('.has-error')
      if (!errorFields.length) return

      // Ensure property nav buttons with index 0 are visible in single_property mode
      this.ensureSinglePropertyNavButtonsVisible()

      // Switch to the property tab containing the first error
      this.switchToPropertyTabWithError(errorFields[0])

      errorFields.forEach((field) => {
        const section = field.closest('section')
        if (!section) return

        // Find all nav buttons matching this section (handles portfolio mode where buttons may be duplicated)
        const sectionButtons = document.querySelectorAll(`button[data-scroll-to-section-id-param="${section.id}"]`)
        sectionButtons.forEach((button) => {
          const icon = button.querySelector('[data-scroll-to-section-target="error"]')
          if (icon) icon.classList.remove('hidden')
        })

        const controller = this.application.getControllerForElementAndIdentifier(section, "collapsable-section")
        if (controller) controller.expand()
      })

      const field = errorFields[0]
      const focusableSelector = 'input:not([type=hidden]), select, textarea, button'
      const focusable = field.querySelector(focusableSelector) ||
        (field.matches(focusableSelector) ? field : null)
      if (focusable) focusable.focus()
    })
  }

  switchToPropertyTabWithError(errorField) {
    const reportSection = errorField.closest('[data-multi-property-target="reportSection"]')
    if (!reportSection) return

    const multiPropertyElement = document.querySelector('[data-controller~="multi-property"]')
    if (!multiPropertyElement) return

    const multiPropertyController = this.application.getControllerForElementAndIdentifier(
      multiPropertyElement,
      'multi-property'
    )
    if (!multiPropertyController) return

    const isSingleProperty = multiPropertyController.getCurrentMultiPropertyType() === 'single_property'
    if (isSingleProperty) return

    const reportSections = Array.from(
      document.querySelectorAll('[data-multi-property-target="reportSection"]')
    )
    const reportIndex = reportSections.indexOf(reportSection)

    if (reportIndex >= 0) {
      multiPropertyController.switchToView('report', reportIndex)
    }
  }

  ensureSinglePropertyNavButtonsVisible() {
    const hiddenField = document.querySelector('input[type="hidden"][name*="multi_property_type"]')
    const multiPropertyType = hiddenField?.value || 'single_property'
    if (multiPropertyType !== 'single_property') return

    const propertyButtons = document.querySelectorAll('[data-multi-property-target="propertySidebarSection"][data-report-index="0"]')
    propertyButtons.forEach((btn) => btn.classList.remove('hidden'))
  }

  onQuoteRequestorChange() {
    this.loadBrokersForLender()
    this.hideShowBrokerContactFields()
    this.hideShowLenderContactFields()
  }

  initializeContactOptions() {
    // On edit mode, refresh contact options if broker/lender are already selected
    if (this.hasLenderContactsTarget && this.lenderTarget.value) {
      const lenderId = this.lenderTarget.value
      const contactsUrl = `/lenders/${lenderId}/contacts.json`
      this.loadJson(contactsUrl, this.processLenderContacts.bind(this))
    }

    if (this.hasBrokerContactsTarget && this.brokerTarget.value) {
      const brokerId = this.brokerTarget.value
      const contactsUrl = `/brokers/${brokerId}/contacts.json`
      this.loadJson(contactsUrl, this.processBrokerContacts.bind(this))
    }
  }

  hideShowBrokerContactFields() {
    if (!this.hasBrokerContactsDivTarget) return

    if (this.quoteRequestorTarget.value === 'Broker' && this.brokerTarget.value) {
      this.brokerContactsDivTarget.classList.remove('hidden')
    } else {
      this.brokerContactsDivTarget.classList.add('hidden')
    }
  }

  hideShowLenderContactFields() {
    if (!this.hasLenderContactsDivTarget) return

    if (this.lenderTarget.value) {
      this.lenderContactsDivTarget.classList.remove('hidden')
    } else {
      this.lenderContactsDivTarget.classList.add('hidden')
    }
  }

  onLenderChange() {
    if (this.lenderTarget.value) {
      const vpid = this.valuationIdValue
      const lenderId = this.lenderTarget.value
      const addresseesUrl = `/lender_addressees.json?lender_id=${lenderId}`
      const contactsUrl = `/lenders/${lenderId}/contacts.json`

      this.lenderEntitiesFrameTarget.classList.remove('hidden')
      this.lenderEntitiesFrameTarget.src = `/lenders/${lenderId}/entities?valuation_id=${vpid}`

      this.loadJson(addresseesUrl, this.processLenderAddressees.bind(this))
      this.loadJson(contactsUrl, this.processLenderContacts.bind(this))
    } else {
      this.lenderEntitiesFrameTarget.classList.add('hidden')
      this.processLenderAddressees([])
      this.processLenderContacts([])
    }

    if (this.hasQuoteRequestorTarget && this.quoteRequestorTarget.value === 'Broker') {
      this.loadBrokersForLender()
    }
    this.hideShowLenderContactFields()
  }

  loadBrokersForLender() {
    if (!this.hasBrokerTarget) return

    const requestorValue = this.hasQuoteRequestorTarget ? this.quoteRequestorTarget.value : ''
    const lenderId = this.hasLenderTarget ? this.lenderTarget.value : ''
    const url = lenderId && requestorValue === 'Broker'
      ? `/brokers.json?for_lender_id=${lenderId}&requestor_value=Broker`
      : '/brokers.json'

    this.loadJson(url, this.processBrokers.bind(this))
  }

  processBrokers(data) {
    if (!this.hasBrokerTarget) return

    const options = data.map((broker) => ({ id: broker.id, name: broker.text || broker.name }))
    this.processSelectData(options, this.brokerTarget, 'single-select-field-component-with-search')

    this.processBrokerContacts([])
    this.hideShowBrokerContactFields()
  }

  onBrokerChange() {
    if (this.brokerTarget.value) {
      const brokerId = this.brokerTarget.value
      const contactsUrl = `/brokers/${brokerId}/contacts.json`

      this.loadJson(contactsUrl, this.processBrokerContacts.bind(this))
    } else {
      this.processBrokerContacts([])
    }

    this.hideShowBrokerContactFields()
  }

  processLenderAddressees(data) {
    const mainAddressee = data.length ? data[0].id : null
    this.processSelectData(data, this.lenderAddresseeTarget, 'single-select-field-component-with-search', mainAddressee)
  }

  processLenderContacts(data) {
    if (!this.hasLenderContactsTarget) return
    this.processSelectData(data, this.lenderContactsTarget, 'multi-select-chips-with-search')
  }

  processBrokerContacts(data) {
    if (!this.hasBrokerContactsTarget) return
    this.processSelectData(data, this.brokerContactsTarget, 'multi-select-chips-with-search')
  }

  processSelectData(data, targetElement, controllerName, selectedValue = null) {
    requestAnimationFrame(() => {
      const controller = this.application.getControllerForElementAndIdentifier(
        targetElement.closest(`div[data-controller="${controllerName}"]`),
        controllerName
      )
      if (controller) {
        controller.refreshOptions(data)
        if (selectedValue) controller.selectItem(selectedValue)
      }
    })
  }

  async loadJson(url, callback) {
    try {
      const response = await fetch(url)
      const data = await response.json()

      callback(data)
    } catch (error) {
      console.error("Fetch error:", error)
    }
  }

  async toggleLedByApi(event) {
    if ('' + this.ledByApiOriginalValue != '' + this.ledByApiTarget.checked) {
      event.preventDefault();
      const state = this.ledByApiTarget.checked;
      const confirmController = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller~="confirm"]'),
        'confirm',
      );
      const ok = await confirmController.request({ message: this.ledByApiMessageValue });

      if (ok) this.ledByApiTarget.checked = state;

      this.ledByApiTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}
